/**
 * MIClockController — wraps Cesium's `viewer.clock` so consumers can drive
 * scene time without touching Cesium APIs. Fires 'tick' events on every
 * Cesium clock tick. Cesium is resolved through the centralised
 * `util/cesium` helper so the same engine instance is used as MISceneView.
 */

import { EventEmitter } from '../events/MapEvent';
import { getCesium } from '../util/cesium';
import { MIDiagnostics } from '../diagnostics/MIDiagnostics';

export interface MIClockRange {
  start: Date;
  stop: Date;
  /** What happens when currentTime hits stop: 'unbounded' | 'clamped' | 'loop' */
  clampMode?: 'unbounded' | 'clamped' | 'loop';
}

export class MIClockController extends EventEmitter {
  private _clock: any;
  private _removeTickListener: (() => void) | null = null;

  constructor(nativeClock: any | null) {
    super();
    this._clock = nativeClock;
    if (nativeClock?.onTick?.addEventListener) {
      const onTick = (clock: any) => {
        this.emit('tick', {
          currentTime: this.currentTime,
          multiplier: clock.multiplier,
          shouldAnimate: clock.shouldAnimate,
        });
      };
      nativeClock.onTick.addEventListener(onTick);
      this._removeTickListener = () => { try { nativeClock.onTick.removeEventListener(onTick); } catch {} };
    }
  }

  get currentTime(): Date {
    if (this._clock && getCesium()) {
      try { return getCesium().JulianDate.toDate(this._clock.currentTime); }
      catch (e) { MIDiagnostics.warn('MIClockController', 'currentTime read via JulianDate.toDate failed', e as Error); }
    }
    return new Date();
  }
  set currentTime(d: Date) {
    if (this._clock && getCesium()) {
      try { this._clock.currentTime = getCesium().JulianDate.fromDate(d); }
      catch (e) { MIDiagnostics.warn('MIClockController', 'currentTime write via JulianDate.fromDate failed', e as Error); }
    }
  }

  get multiplier(): number { return this._clock?.multiplier ?? 1; }
  set multiplier(m: number) { if (this._clock) this._clock.multiplier = m; }

  get shouldAnimate(): boolean { return !!this._clock?.shouldAnimate; }
  set shouldAnimate(v: boolean) { if (this._clock) this._clock.shouldAnimate = v; }

  start(): void { if (this._clock) this._clock.shouldAnimate = true; }
  stop(): void { if (this._clock) this._clock.shouldAnimate = false; }

  /**
   * Get the active animation range (start/stop/clampMode), or null when no
   * native clock is wired up.
   */
  get range(): MIClockRange | null {
    const C = getCesium();
    if (!this._clock || !C) return null;
    try {
      const start = C.JulianDate.toDate(this._clock.startTime);
      const stop = C.JulianDate.toDate(this._clock.stopTime);
      const mode: MIClockRange['clampMode'] =
        this._clock.clockRange === C.ClockRange?.LOOP_STOP ? 'loop' :
        this._clock.clockRange === C.ClockRange?.UNBOUNDED ? 'unbounded' : 'clamped';
      return { start, stop, clampMode: mode };
    } catch (e) {
      MIDiagnostics.warn('MIClockController', 'range read failed', e as Error);
      return null;
    }
  }
  set range(r: MIClockRange | null) { if (r) this.setRange(r); }

  setRange(range: MIClockRange): void {
    if (!this._clock || !getCesium()) return;
    try {
      this._clock.startTime = getCesium().JulianDate.fromDate(range.start);
      this._clock.stopTime = getCesium().JulianDate.fromDate(range.stop);
      const mode = range.clampMode ?? 'clamped';
      this._clock.clockRange =
        mode === 'loop' ? getCesium().ClockRange.LOOP_STOP :
        mode === 'unbounded' ? getCesium().ClockRange.UNBOUNDED :
        getCesium().ClockRange.CLAMPED;
    } catch (e) {
      MIDiagnostics.warn('MIClockController', 'setRange failed', e as Error);
    }
  }

  /** Step the clock forward by `seconds`. */
  step(seconds: number): void {
    const C = getCesium();
    if (!this._clock || !C) return;
    try {
      const scratch = new C.JulianDate();
      this._clock.currentTime = C.JulianDate.addSeconds(this._clock.currentTime, seconds, scratch);
    } catch (e) {
      MIDiagnostics.warn('MIClockController', 'step() failed (JulianDate.addSeconds)', e as Error);
    }
  }

  destroy(): void {
    if (this._removeTickListener) { try { this._removeTickListener(); } catch {} this._removeTickListener = null; }
    this.removeAllListeners();
  }
}
